import { useEffect, useState } from "react";
import { SparkleIcon } from "./ui.js";

/**
 * Step 2 — the "we're building your profile" moment. The backend call is a
 * single request (~10-20s: crawl + LLM), so this is a timed, cosmetic checklist
 * that advances on its own to make the wait feel like work being done for them.
 * The wizard swaps to the review step as soon as the request resolves.
 */
const STAGES = [
  "Reading your website…",
  "Finding the services you offer…",
  "Matching them to local job categories…",
  "Pulling in your photos & details…",
  "Assembling your profile…",
];

export function GeneratingStep({ websiteUrl }: { websiteUrl: string }) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setActive((i) => Math.min(i + 1, STAGES.length - 1));
    }, 2600);
    return () => clearInterval(id);
  }, []);

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-6 py-12">
      <div className="flex justify-center">
        <span className="flex h-16 w-16 animate-pulse items-center justify-center rounded-2xl bg-brand-50 text-brand-700">
          <SparkleIcon className="h-8 w-8" />
        </span>
      </div>
      <h1 className="mt-6 text-center text-2xl font-bold text-charcoal">Building your profile</h1>
      {websiteUrl && (
        <p className="mt-2 truncate text-center text-sm text-charcoal">{prettyHost(websiteUrl)}</p>
      )}

      <ol className="mx-auto mt-8 w-full max-w-xs space-y-3">
        {STAGES.map((stage, i) => {
          const done = i < active;
          const current = i === active;
          return (
            <li key={stage} className="flex items-center gap-3">
              <span
                className={[
                  "flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs",
                  done
                    ? "border-brand-600 bg-brand-600 text-white"
                    : current
                      ? "border-brand-600 text-brand-700"
                      : "border-charcoal/30 text-charcoal",
                ].join(" ")}
              >
                {done ? "✓" : current ? <Spinner /> : ""}
              </span>
              <span
                className={["text-sm", done || current ? "text-charcoal" : "text-charcoal"].join(
                  " ",
                )}
              >
                {stage}
              </span>
            </li>
          );
        })}
      </ol>
    </main>
  );
}

function Spinner() {
  return (
    <svg className="h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" opacity=".25" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

function prettyHost(url: string): string {
  try {
    return new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`).hostname.replace(
      /^www\./,
      "",
    );
  } catch {
    return url;
  }
}
